import { ChevronDown, Filter, MapPin } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import Map, { Layer, LayerProps, MapLayerMouseEvent, MapRef, Marker, NavigationControl, Source } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const CANDIDATE_SOURCE_ID = "location-map-candidates";
const CANDIDATE_LAYER_ID = "location-map-candidates-points";

const unclusteredPointLayer: LayerProps = {
    id: CANDIDATE_LAYER_ID,
    type: "circle",
    source: CANDIDATE_SOURCE_ID,
    paint: {
        "circle-color": ["get", "color"],
        "circle-radius": 7,
        "circle-stroke-width": 2,
        "circle-stroke-color": "#fff"
    }
};

type LocationMapCandidate = {
    id: string | number;
    longitude?: number | string | null;
    latitude?: number | string | null;
    category: string;
    color: string;
    tooltip: string;
    url: string;
};

type LocationMapCandidateFilter = {
    key: string;
    label: string;
    description?: string;
    color: string;
    count: number;
};

type LocationMapProps = {
    latitude: number;
    longitude: number;
    address?: string;
    company_name?: string;
    candidates?: LocationMapCandidate[];
    candidateFilters?: LocationMapCandidateFilter[];
    candidateRadiusKm?: number;
};

export function LocationMap({ latitude, longitude, address, company_name, candidates, candidateFilters, candidateRadiusKm }: LocationMapProps) {
    const mapRef = useRef<MapRef>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(new Set());
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

    const candidateGeojson = useMemo(() => ({
        type: "FeatureCollection" as const,
        features: (candidates ?? []).filter(c => !hiddenCategories.has(c.category)).flatMap(c => {
            const lon = Number(c.longitude);
            const lat = Number(c.latitude);
            if (!Number.isFinite(lon) || !Number.isFinite(lat)) return [];
            return [{
                type: "Feature" as const,
                properties: {
                    id: String(c.id),
                    category: c.category,
                    color: c.color,
                    tooltip: c.tooltip,
                    url: c.url
                },
                geometry: { type: "Point" as const, coordinates: [lon, lat] }
            }];
        })
    }), [candidates, hiddenCategories]);

    const onCandidateEnter = (event: MapLayerMouseEvent) => {
        const text = event.features?.[0]?.properties?.tooltip;
        if (typeof text === "string") setTooltip({ text, x: event.point.x, y: event.point.y });
    };

    const onCandidateClick = (event: MapLayerMouseEvent) => {
        const url = event.features?.[0]?.properties?.url;
        if (typeof url === "string") window.location.assign(url);
    };

    useEffect(() => {
        if (mapRef.current && latitude && longitude) {
            // Center the map on the location once loaded
            const map = mapRef.current.getMap();
            if (map && isLoaded) {
                if (candidateRadiusKm) {
                    // Frame the whole area candidates were selected from, otherwise most of
                    // them sit outside the initial viewport.
                    const latDelta = candidateRadiusKm / 111.32;
                    const lngDelta = latDelta / Math.max(Math.cos((latitude * Math.PI) / 180), 0.01);
                    map.fitBounds(
                        [
                            [longitude - lngDelta, latitude - latDelta],
                            [longitude + lngDelta, latitude + latDelta]
                        ],
                        { duration: 1000, padding: 20 }
                    );
                    return;
                }
                map.flyTo({
                    center: [longitude, latitude],
                    zoom: 13,
                    duration: 1000,
                });
            }
        }
    }, [latitude, longitude, isLoaded, candidateRadiusKm]);

    if (!latitude || !longitude) {
        return (
            <div className="flex h-[300px] items-center justify-center rounded-md bg-gray-50 text-gray-500">
                <div className="flex flex-col items-center gap-2">
                    <MapPin className="h-8 w-8" />
                    <span className="text-sm">Coordonnées GPS non disponibles</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex w-full flex-col gap-2">
            {(company_name || address) && (
                <div className="flex flex-col gap-1">
                    {company_name && <h3 className="text-sm font-semibold text-gray-900">{company_name}</h3>}
                    {address && <p className="text-xs text-gray-600">{address}</p>}
                </div>
            )}
            <div className="relative h-[400px] w-full overflow-hidden rounded-md border border-gray-200">
                {candidateFilters && candidateFilters.length > 0 && (
                    <div className="absolute left-3 top-3 z-10 rounded-md border bg-white/95 shadow">
                        <button
                            type="button"
                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium"
                            onClick={() => setFiltersOpen(open => !open)}
                        >
                            <Filter className="size-4" />
                            Filtrer
                            <ChevronDown className={`size-4 transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
                        </button>
                        {filtersOpen && (
                            <div className="space-y-1.5 border-t px-3 py-2">
                                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                                    <input
                                        type="checkbox"
                                        checked={hiddenCategories.size === 0}
                                        onChange={() => setHiddenCategories(hiddenCategories.size === 0 ? new Set(candidateFilters.map(filter => filter.key)) : new Set())}
                                    />
                                    Tous
                                </label>
                                {candidateFilters.map(filter => (
                                    <label key={filter.key} title={filter.description} className="flex cursor-pointer items-center gap-2 text-xs">
                                        <input
                                            type="checkbox"
                                            checked={!hiddenCategories.has(filter.key)}
                                            onChange={() => setHiddenCategories(current => {
                                                const next = new Set(current);
                                                next.has(filter.key) ? next.delete(filter.key) : next.add(filter.key);
                                                return next;
                                            })}
                                        />
                                        <span className="size-2.5 rounded-full" style={{ backgroundColor: filter.color }} />
                                        <span>{filter.label} ({filter.count})</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {tooltip && (
                    <div
                        className="pointer-events-none absolute z-20 rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground shadow"
                        style={{ left: tooltip.x + 10, top: tooltip.y - 25 }}
                    >
                        {tooltip.text}
                    </div>
                )}
                <Map
                    ref={mapRef}
                    mapboxAccessToken="pk.eyJ1Ijoid2wtamVhbmJhcHRpc3RlY2FwbGFuIiwiYSI6ImNsdXd2cmZyajAwOG4yaHAxY2J3bnBud28ifQ.XJxHTOB4SWUiaeSzWLnPWg"
                    initialViewState={{
                        longitude: longitude,
                        latitude: latitude,
                        zoom: 13,
                    }}
                    cursor={tooltip ? "pointer" : "auto"}
                    interactiveLayerIds={candidateGeojson.features.length > 0 ? [CANDIDATE_LAYER_ID] : []}
                    style={{ width: "100%", height: "100%" }}
                    mapStyle="mapbox://styles/wl-jeanbaptistecaplan/clux983ul003701oc91ntfrw3"
                    onLoad={() => setIsLoaded(true)}
                    onClick={onCandidateClick}
                    onMouseEnter={onCandidateEnter}
                    onMouseLeave={() => setTooltip(null)}
                >
                    {candidateGeojson.features.length > 0 && (
                        <Source id={CANDIDATE_SOURCE_ID} type="geojson" data={candidateGeojson}>
                            <Layer {...unclusteredPointLayer} />
                        </Source>
                    )}
                    <Marker longitude={longitude} latitude={latitude} anchor="bottom">
                        <div className="flex flex-col items-center">
                            <div className="rounded-full bg-red-500 p-2 shadow-lg">
                                <MapPin className="h-5 w-5 text-white" />
                            </div>
                        </div>
                    </Marker>
                    <NavigationControl position="top-right" />
                </Map>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
                <MapPin className="h-3 w-3" />
                <span>
                    {latitude.toFixed(6)}, {longitude.toFixed(6)}
                </span>
            </div>
        </div>
    );
}
